import { useEffect, useState } from "react";
import "./SettingRow.css";
export function SettingRow({
  settingKey,
  option,
  section,
  updateSetting,
  resetSetting,
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(option.value);
  const [unit, setUnit] = useState(option.unit || "");

  useEffect(() => {
    setValue(option.value);
    setUnit(option.unit || "");
  }, [option.value, option.unit]);

  const validateValue = () => {
    const type = option.type;

    if (type === "number") {
      if (!/^\d+$/.test(String(value))) {
        return "Value must be a whole number";
      }
    }

    if (type === "float") {
      if (isNaN(Number(value))) {
        return "Value must be a decimal number";
      }
    }

    if (type === "boolean") {
      if (value !== true && value !== false) {
        return "Value must be true or false";
      }
    }

    if (type === "time_range") {
      if (!value?.start && !value?.end) {
        return null;
      }

      if (!value?.start || !value?.end) {
        return "Please select both start and end time";
      }
    }

    return null;
  };

  const saveValue = () => {
    const error = validateValue();

    if (error) {
      alert(`${settingKey}: ${error}`);

      setValue(option.value);
      setUnit(option.unit || "");
      setEditing(false);

      return;
    }

    updateSetting(
      section,
      settingKey,
      value,
      option.unit_options ? unit : null,
    );

    setEditing(false);
  };

  const cancelEdit = () => {
    setValue(option.value);
    setUnit(option.unit || "");
    setEditing(false);
  };

  const confirmReset = () => {
    const answer = window.confirm(`Reset "${settingKey}" back to default?`);

    if (answer) {
      resetSetting(section, settingKey);
    }
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) {
      return "none";
    }

    if (typeof val === "object") {
      const start = val?.start;
      const end = val?.end;

      if (!start && !end) {
        return "none";
      }

      if (start && end) {
        return `${start} → ${end}`;
      }

      if (start) {
        return `${start} → --:--`;
      }

      if (end) {
        return `--:-- → ${end}`;
      }
    }

    return String(val);
  };

  const modified =
    JSON.stringify(option.default) !== JSON.stringify(option.value) ||
    (option.unit !== undefined && option.unit !== option.default_unit);

  return (
    <div className="setting-row">
      {/* SETTING */}
      <div className="setting-name">{settingKey.replaceAll("_", " ")}</div>

      {/* DEFAULT */}
      <div className="default-value">
        {formatValue(option.default)}

        {option.unit_options && option.default_unit && (
          <span className="unit-text"> {option.default_unit}</span>
        )}
      </div>

      {/* CURRENT */}
      <div
        className="current-value"
        onDoubleClick={() => {
          if (!editing) {
            setEditing(true);
          }
        }}
      >
        {editing ? (
          option.type === "time_range" ? (
            /* =========================
               TIME RANGE EDITOR
            ========================= */
            <div className="time-range-editor">
              <input
                type="time"
                value={value?.start || ""}
                onChange={(e) =>
                  setValue({
                    ...(value || {}),
                    start: e.target.value || null,
                  })
                }
              />

              <span className="range-arrow">→</span>

              <input
                type="time"
                value={value?.end || ""}
                onChange={(e) =>
                  setValue({
                    ...(value || {}),
                    end: e.target.value || null,
                  })
                }
              />

              <button
                type="button"
                className="save-value-btn"
                onClick={saveValue}
              >
                ✓
              </button>

              <button
                type="button"
                className="cancel-value-btn"
                onClick={cancelEdit}
              >
                ✕
              </button>
            </div>
          ) : (
            /* =========================
               NORMAL VALUE EDITOR
            ========================= */
            <div className="setting-value-editor">
              <input
                autoFocus
                type={option.type === "number" ? "number" : "text"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    saveValue();
                  }

                  if (e.key === "Escape") {
                    cancelEdit();
                  }
                }}
              />

              {/* UNIT SELECT */}
              {option.unit_options && option.unit_options.length > 0 && (
                <select
                  className="unit-select"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                >
                  {option.unit_options.map((unitOption) => (
                    <option key={unitOption} value={unitOption}>
                      {unitOption}
                    </option>
                  ))}
                </select>
              )}

              {/* SAVE */}
              <button
                type="button"
                className="save-value-btn"
                onClick={saveValue}
              >
                ✓
              </button>

              {/* CANCEL */}
              <button
                type="button"
                className="cancel-value-btn"
                onClick={cancelEdit}
              >
                ✕
              </button>
            </div>
          )
        ) : (
          /* =========================
             DISPLAY
          ========================= */
          <div className="display-value">
            <span>{formatValue(option.value)}</span>

            {option.unit && <span className="unit-text"> {option.unit}</span>}
          </div>
        )}
      </div>

      {/* STATUS */}
      <div>
        {modified ? (
          <span className="modified">Modified</span>
        ) : (
          <span className="default">Default</span>
        )}
      </div>

      {/* ACTION */}
      <div className="action-cell">
        <button
          className="reset-btn"
          onClick={confirmReset}
          title="Reset to default"
        >
          ↺
        </button>
      </div>
    </div>
  );
}
